import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from '../../utils/axios';

const API = '/api/delivery';

export const fetchPackages = createAsyncThunk('delivery/fetchPackages',
  async ({ sourceType } = {}, { rejectWithValue }) => {
    try {
      const params = {};
      if (sourceType) params.source_type = sourceType;
      const { data } = await axios.get(`${API}/packages`, { params });
      return data;
    } catch (e) { return rejectWithValue(e.response?.data?.detail || e.message); }
  }
);

export const createPackage = createAsyncThunk('delivery/createPackage',
  async (packageData, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(`${API}/packages`, packageData);
      return data;
    } catch (e) { return rejectWithValue(e.response?.data?.detail || e.message); }
  }
);

export const revokePackage = createAsyncThunk('delivery/revokePackage',
  async (packageId, { rejectWithValue }) => {
    try {
      await axios.post(`${API}/packages/${packageId}/revoke`);
      return packageId;
    } catch (e) { return rejectWithValue(e.response?.data?.detail || e.message); }
  }
);

export const createShare = createAsyncThunk('delivery/createShare',
  async (shareData, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(`${API}/shares`, shareData);
      return data;
    } catch (e) { return rejectWithValue(e.response?.data?.detail || e.message); }
  }
);

export const fetchShares = createAsyncThunk('delivery/fetchShares',
  async ({ packageId } = {}, { rejectWithValue }) => {
    try {
      const params = {};
      if (packageId) params.package_id = packageId;
      const { data } = await axios.get(`${API}/shares`, { params });
      return data;
    } catch (e) { return rejectWithValue(e.response?.data?.detail || e.message); }
  }
);

export const revokeShare = createAsyncThunk('delivery/revokeShare',
  async (shareId, { rejectWithValue }) => {
    try {
      await axios.post(`${API}/shares/${shareId}/revoke`);
      return shareId;
    } catch (e) { return rejectWithValue(e.response?.data?.detail || e.message); }
  }
);

export const fetchDownloadEvents = createAsyncThunk('delivery/fetchEvents',
  async ({ packageId, shareId } = {}, { rejectWithValue }) => {
    try {
      const params = {};
      if (packageId) params.package_id = packageId;
      if (shareId) params.share_id = shareId;
      const { data } = await axios.get(`${API}/events`, { params });
      return data;
    } catch (e) { return rejectWithValue(e.response?.data?.detail || e.message); }
  }
);

export const fetchDeliveryNodes = createAsyncThunk('delivery/fetchNodes',
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(`${API}/nodes`);
      return data;
    } catch (e) { return rejectWithValue(e.response?.data?.detail || e.message); }
  }
);

export const createDeliveryNode = createAsyncThunk('delivery/createNode',
  async (nodeData, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(`${API}/nodes`, nodeData);
      return data;
    } catch (e) { return rejectWithValue(e.response?.data?.detail || e.message); }
  }
);

export const provisionDeliveryNode = createAsyncThunk('delivery/provisionNode',
  async ({ nodeId, config }, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(`${API}/nodes/${nodeId}/provision`, config || {}, { timeout: 300000 });
      return data;
    } catch (e) { return rejectWithValue(e.response?.data?.detail || e.message); }
  }
);

export const checkDeliveryNodeHealth = createAsyncThunk('delivery/checkNodeHealth',
  async (nodeId, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(`${API}/nodes/${nodeId}/check-health`);
      return data;
    } catch (e) { return rejectWithValue(e.response?.data?.detail || e.message); }
  }
);

const deliverySlice = createSlice({
  name: 'delivery',
  initialState: {
    packages: [],
    shares: [],
    events: [],
    nodes: [],
    loading: false,
    error: null,
  },
  reducers: {
    clearDeliveryError: (state) => { state.error = null; },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPackages.pending, (state) => { state.loading = true; })
      .addCase(fetchPackages.fulfilled, (state, action) => { state.loading = false; state.packages = action.payload; })
      .addCase(fetchPackages.rejected, (state, action) => { state.loading = false; state.error = action.payload; })
      .addCase(createPackage.fulfilled, (state, action) => { state.packages.unshift(action.payload); })
      .addCase(revokePackage.fulfilled, (state, action) => {
        const pkg = state.packages.find(p => p.id === action.payload);
        if (pkg) pkg.revoked = true;
      })
      .addCase(fetchShares.fulfilled, (state, action) => { state.shares = action.payload; })
      .addCase(createShare.fulfilled, (state, action) => { state.shares.unshift(action.payload); })
      .addCase(revokeShare.fulfilled, (state, action) => {
        const share = state.shares.find(s => s.id === action.payload);
        if (share) share.revoked_at = new Date().toISOString();
      })
      .addCase(fetchDownloadEvents.fulfilled, (state, action) => { state.events = action.payload; })
      .addCase(fetchDeliveryNodes.fulfilled, (state, action) => { state.nodes = action.payload; })
      .addCase(createDeliveryNode.fulfilled, (state, action) => { state.nodes.push(action.payload); });
  },
});

export const { clearDeliveryError } = deliverySlice.actions;
export default deliverySlice.reducer;
